import { CoordinateSystem } from '@awayjs/core';
import { ContextGLBlendFactor } from '../base/ContextGLBlendFactor';
import { ContextGLDrawMode } from '../base/ContextGLDrawMode';
import { ContextGLClearMask } from '../base/ContextGLClearMask';
import { ContextGLCompareMode } from '../base/ContextGLCompareMode';
import { ContextGLProgramType } from '../base/ContextGLProgramType';
import { ContextGLStencilAction } from '../base/ContextGLStencilAction';
import { ContextGLTriangleFace } from '../base/ContextGLTriangleFace';
import { ContextGLVertexBufferFormat } from '../base/ContextGLVertexBufferFormat';
import { ContextWebGLFlags } from './ContextWebGLFlags';
import { CubeTextureWebGL } from './CubeTextureWebGL';
import { IndexBufferWebGL } from './IndexBufferWebGL';
import { ProgramWebGL } from './ProgramWebGL';
import { TextureWebGL } from './TextureWebGL';
import { VertexBufferWebGL } from './VertexBufferWebGL';
import { TextureContextWebGL } from './TextureContextWebGL';
import { VaoContextWebGL, VaoWebGL } from './VaoWebGL';
import { State } from './State';
import { Settings } from '../Settings';
import { FenceContextWebGL } from './FenceContextWebGL';
var _DEBUG_renderMode = '';
//@ts-ignore
window._AWAY_DEBUG_ = Object.assign(window._AWAY_DEBUG_ || {}, {
    forceLineMode: function (lineMode) {
        if (lineMode === void 0) { lineMode = false; }
        _DEBUG_renderMode = lineMode ? 'line' : '';
    }
});
var ContextWebGL = /** @class */ (function () {
    function ContextWebGL(canvas, alpha) {
        if (alpha === void 0) { alpha = false; }
        this._blendFactorMap = new Object();
        this._drawModeMap = new Object();
        this._compareModeMap = new Object();
        this._stencilActionMap = new Object();
        this._vertexBufferMap = {};
        this._drawing = true;
        // [x, y, w, h]
        this._viewportState = new State(0, 0, 0, 0);
        // [enable, func, src, dst];
        this._blendState = new State(0, 0, 0, 0);
        // [r, g, b, a]
        this._colorMapState = new State(true, true, true, true);
        // [r, g, b, a]
        this._clearColorState = new State(0, 0, 0, 0);
        // [stencil, depth], i don't know default a depth value, set -1, to force change it
        this._clearSD = new State(0, -1);
        // [enable, mask, func]
        this._depthState = new State(0, 0, 0);
        // [enable, mask, func]
        this._stencilState = new State(0, 1, 0);
        // [enable, face]
        this._cullState = new State(0, 0, 0);
        this._stencilReferenceValue = 0;
        this._stencilReadMask = 0xff;
        this._separateStencil = false;
        this.lastBoundedIndexBuffer = null;
        this._hasVao = false;
        this._container = canvas;
        this.initWebGL(alpha);
    }
    Object.defineProperty(ContextWebGL.prototype, "hasFence", {
        get: function () {
            return !!this._fenceContext;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(ContextWebGL.prototype, "hasVao", {
        get: function () {
            return this._hasVao;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(ContextWebGL.prototype, "glVersion", {
        get: function () {
            return this._glVersion;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(ContextWebGL.prototype, "pixelRatio", {
        get: function () {
            return this._pixelRatio;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(ContextWebGL.prototype, "container", {
        get: function () {
            return this._container;
        },
        set: function (value) {
            this._container = value;
            this.initWebGL();
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(ContextWebGL.prototype, "standardDerivatives", {
        get: function () {
            return this._standardDerivatives;
        },
        enumerable: false,
        configurable: true
    });
    ContextWebGL.prototype.initWebGL = function (alpha) {
        var _a, _b, _c, _d, _e;
        if (alpha === void 0) { alpha = false; }
        var props = {
            alpha: alpha,
            //antialias: (/\bCrOS\b/.test(navigator.userAgent))? false : true,
            stencil: true
        };
        try {
            if (ContextWebGLFlags.PREF_VERSION === 2 /* WEBGL2 */) {
                this._gl = this._container.getContext('webgl2', props);
                this._glVersion = 2;
            }
            if (!this._gl) {
                this._gl = (this._container.getContext('webgl', props)
                    || this._container.getContext('experimental-webgl', props));
                this._glVersion = 1;
                if (ContextWebGLFlags.PREF_VERSION === 2 /* WEBGL2 */) {
                    console.warn('[CONTEXT] Preferred WebGL2 not supported on you device. WebGL1 will used!');
                }
            }
        }
        catch (e) {
            //this.dispatchEvent( new away.events.AwayEvent( away.events.AwayEvent.INITIALIZE_FAILED, e ) );
        }
        if (this._gl) {
            //this.dispatchEvent( new away.events.AwayEvent( away.events.AwayEvent.INITIALIZE_SUCCESS ) );
            var gl = this._gl;
            this._standardDerivatives = this._glVersion === 2
                || !!gl.getExtension('OES_standard_derivatives');
            /* eslint-disable */
            //setup shortcut dictionaries
            var BF = ContextGLBlendFactor;
            this._blendFactorMap = (_a = {},
                _a[BF.ONE] = gl.ONE,
                _a[BF.DESTINATION_ALPHA] = gl.DST_ALPHA,
                _a[BF.DESTINATION_COLOR] = gl.DST_COLOR,
                _a[BF.ONE] = gl.ONE,
                _a[BF.ONE_MINUS_DESTINATION_ALPHA] = gl.ONE_MINUS_DST_ALPHA,
                _a[BF.ONE_MINUS_DESTINATION_COLOR] = gl.ONE_MINUS_DST_COLOR,
                _a[BF.ONE_MINUS_SOURCE_ALPHA] = gl.ONE_MINUS_SRC_ALPHA,
                _a[BF.ONE_MINUS_SOURCE_COLOR] = gl.ONE_MINUS_SRC_COLOR,
                _a[BF.SOURCE_ALPHA] = gl.SRC_ALPHA,
                _a[BF.SOURCE_COLOR] = gl.SRC_COLOR,
                _a[BF.ZERO] = gl.ZERO,
                _a);
            this._drawModeMap = (_b = {},
                _b[ContextGLDrawMode.LINES] = gl.LINES,
                _b[ContextGLDrawMode.TRIANGLES] = gl.TRIANGLES,
                _b);
            var CM = ContextGLCompareMode;
            this._compareModeMap = (_c = {},
                _c[CM.ALWAYS] = gl.ALWAYS,
                _c[CM.EQUAL] = gl.EQUAL,
                _c[CM.GREATER] = gl.GREATER,
                _c[CM.GREATER_EQUAL] = gl.GEQUAL,
                _c[CM.LESS] = gl.LESS,
                _c[CM.LESS_EQUAL] = gl.LEQUAL,
                _c[CM.NEVER] = gl.NEVER,
                _c[CM.NOT_EQUAL] = gl.NOTEQUAL,
                _c);
            var SA = ContextGLStencilAction;
            this._stencilActionMap = (_d = {},
                _d[SA.DECREMENT_SATURATE] = gl.DECR,
                _d[SA.DECREMENT_WRAP] = gl.DECR_WRAP,
                _d[SA.INCREMENT_SATURATE] = gl.INCR,
                _d[SA.INCREMENT_WRAP] = gl.INCR_WRAP,
                _d[SA.INVERT] = gl.INVERT,
                _d[SA.KEEP] = gl.KEEP,
                _d[SA.SET] = gl.REPLACE,
                _d[SA.ZERO] = gl.ZERO,
                _d);
            var VBF = ContextGLVertexBufferFormat;
            this._vertexBufferMap = (_e = {},
                _e[VBF.FLOAT_1] = new VertexBufferProperties(1, gl.FLOAT, false),
                _e[VBF.FLOAT_2] = new VertexBufferProperties(2, gl.FLOAT, false),
                _e[VBF.FLOAT_3] = new VertexBufferProperties(3, gl.FLOAT, false),
                _e[VBF.FLOAT_4] = new VertexBufferProperties(4, gl.FLOAT, false),
                _e[VBF.BYTE_1] = new VertexBufferProperties(1, gl.BYTE, true),
                _e[VBF.BYTE_2] = new VertexBufferProperties(2, gl.BYTE, true),
                _e[VBF.BYTE_3] = new VertexBufferProperties(3, gl.BYTE, true),
                _e[VBF.BYTE_4] = new VertexBufferProperties(4, gl.BYTE, true),
                _e[VBF.UNSIGNED_BYTE_1] = new VertexBufferProperties(1, gl.UNSIGNED_BYTE, true),
                _e[VBF.UNSIGNED_BYTE_2] = new VertexBufferProperties(2, gl.UNSIGNED_BYTE, true),
                _e[VBF.UNSIGNED_BYTE_3] = new VertexBufferProperties(3, gl.UNSIGNED_BYTE, true),
                _e[VBF.UNSIGNED_BYTE_4] = new VertexBufferProperties(4, gl.UNSIGNED_BYTE, true),
                _e[VBF.SHORT_1] = new VertexBufferProperties(1, gl.SHORT, false),
                _e[VBF.SHORT_2] = new VertexBufferProperties(2, gl.SHORT, false),
                _e[VBF.SHORT_3] = new VertexBufferProperties(3, gl.SHORT, false),
                _e[VBF.SHORT_4] = new VertexBufferProperties(4, gl.SHORT, false),
                _e[VBF.UNSIGNED_SHORT_1] = new VertexBufferProperties(1, gl.UNSIGNED_SHORT, false),
                _e[VBF.UNSIGNED_SHORT_2] = new VertexBufferProperties(2, gl.UNSIGNED_SHORT, false),
                _e[VBF.UNSIGNED_SHORT_3] = new VertexBufferProperties(3, gl.UNSIGNED_SHORT, false),
                _e[VBF.UNSIGNED_SHORT_4] = new VertexBufferProperties(4, gl.UNSIGNED_SHORT, false),
                _e);
            this._stencilCompareMode = gl.ALWAYS;
            this._stencilCompareModeBack = gl.ALWAYS;
            this._stencilCompareModeFront = gl.ALWAYS;
            /* eslint-enable */
            var dpr = window.devicePixelRatio || 1;
            // deprecated a long time ago.
            /*
            const bsr: number = gl['webkitBackingStorePixelRatio'] ||
                gl['mozBackingStorePixelRatio'] ||
                gl['msBackingStorePixelRatio'] ||
                gl['oBackingStorePixelRatio'] ||
                gl['backingStorePixelRatio'] || 1;
            */
            this._pixelRatio = dpr;
        }
        else {
            //this.dispatchEvent( new away.events.AwayEvent( away.events.AwayEvent.INITIALIZE_FAILED, e ) );
            alert('WebGL is not available.');
        }
        this._texContext = new TextureContextWebGL(this);
        if (Settings.ENABLE_VAO) {
            if (VaoContextWebGL.isSupported(this._gl)) {
                this._vaoContext = new VaoContextWebGL(this);
                this._hasVao = true;
            }
            else {
                console.warn('[ContextWebGL] VAO isn\'t supported');
            }
        }
        else {
            console.debug('[ContextWebGL] Vao disabled by settings \'ENABLE_VAO\'');
        }
        if (Settings.ENABLE_ASYNC_READ) {
            if (FenceContextWebGL.isSupported(this._gl)) {
                this._fenceContext = new FenceContextWebGL(this._gl);
            }
            else {
                console.warn('[ContextWebGL] FenceSync isn\'t supported');
            }
        }
        else {
            console.debug('[ContextWebGL] FenceSync disabled by settings \'ENABLE_ASYNC_READ\'');
        }
        // first locked state 0,0,0,0
        this._blendState.lock(true);
    };
    ContextWebGL.prototype.gl = function () {
        return this._gl;
    };
    ContextWebGL.prototype.clear = function (red, green, blue, alpha, depth, stencil, mask) {
        if (red === void 0) { red = 0; }
        if (green === void 0) { green = 0; }
        if (blue === void 0) { blue = 0; }
        if (alpha === void 0) { alpha = 1; }
        if (depth === void 0) { depth = 1; }
        if (stencil === void 0) { stencil = 0; }
        if (mask === void 0) { mask = ContextGLClearMask.ALL; }
        if (!this._drawing) {
            this.updateBlendStatus();
            this._drawing = true;
        }
        var glmask = 0;
        if (mask & ContextGLClearMask.COLOR) {
            if (this._clearColorState.set(red, green, blue, alpha)) {
                this._gl.clearColor(red, green, blue, alpha);
            }
            glmask |= this._gl.COLOR_BUFFER_BIT;
        }
        if (mask & ContextGLClearMask.STENCIL) {
            glmask |= this._gl.STENCIL_BUFFER_BIT;
            this._clearSD.setAt(0, stencil) && this._gl.clearStencil(stencil);
        }
        if (mask & ContextGLClearMask.DEPTH) {
            glmask |= this._gl.DEPTH_BUFFER_BIT;
            this._clearSD.setAt(0, depth) && this._gl.clearDepth(depth);
        }
        this._gl.clear(glmask);
    };
    ContextWebGL.prototype.configureBackBuffer = function (width, height, antiAlias, enableDepthAndStencil) {
        if (enableDepthAndStencil === void 0) { enableDepthAndStencil = true; }
        this._width = width * this._pixelRatio;
        this._height = height * this._pixelRatio;
        if (enableDepthAndStencil) {
            this.enableStencil();
            this.enableDepth();
        }
        else {
            this.disableStencil();
            this.disableDepth();
        }
        if (this._viewportState.set(0, 0, this._width, this._height)) {
            this._gl.viewport(0, 0, this._width, this._height);
        }
    };
    ContextWebGL.prototype.createCubeTexture = function (size, format, optimizeForRenderToTexture, streamingLevels) {
        if (streamingLevels === void 0) { streamingLevels = 0; }
        return new CubeTextureWebGL(this, size);
    };
    ContextWebGL.prototype.createIndexBuffer = function (numIndices) {
        return IndexBufferWebGL.create(this._gl, numIndices);
    };
    ContextWebGL.prototype.createProgram = function () {
        return new ProgramWebGL(this._gl);
    };
    ContextWebGL.prototype.createTexture = function (width, height, format, optimizeForRenderToTexture, streamingLevels) {
        if (streamingLevels === void 0) { streamingLevels = 0; }
        //TODO streaming -- not supported on WebGL
        return TextureWebGL.create(this, width, height);
    };
    ContextWebGL.prototype.createVertexBuffer = function (numVertices, dataPerVertex) {
        return VertexBufferWebGL.create(this._gl, numVertices, dataPerVertex);
    };
    ContextWebGL.prototype.createVao = function () {
        if (!this._hasVao)
            throw 'VAO isn\'n supported';
        return new VaoWebGL(this);
    };
    ContextWebGL.prototype.dispose = function () {
        //
    };
    ContextWebGL.prototype.drawToBitmapImage2D = function (destination, invalidate, async) {
        if (invalidate === void 0) { invalidate = true; }
        if (async === void 0) { async = false; }
        var pixels = new Uint8Array(destination.getDataInternal().buffer);
        var rt = this._texContext._renderTarget;
        var fence = this._fenceContext;
        var width = destination.width, height = destination.height;
        var promise;
        if (async && !fence) {
            promise = Promise.resolve(false);
        }
        if (rt === null || rt === void 0 ? void 0 : rt.multisampled) {
            // because RT is MSAA, we should blit it to no-MSAA and use noMSAA framebufer for reading
            this._texContext.presentFrameBuffer(rt);
            this._gl.bindFramebuffer(this._gl.FRAMEBUFFER, rt.textureFramebuffer);
        }
        if (async && fence) {
            // tasking a PBO read async
            // http://www.songho.ca/opengl/gl_pbo.html
            promise = fence
                .readPixels(0, 0, width, height)
                .then(function (pbo) {
                pbo.read(pixels);
                if (invalidate) {
                    destination.invalidateGPU();
                }
                // restore PBO to fence pool
                // but we can destroy it too
                fence.release(pbo);
                return true;
            });
        }
        else {
            // we MUST unbound all bounded PIXEL_PACK buffer to avoid warnings
            if (fence) {
                fence.unboundAll();
            }
            // no support or not required as async, use sync operation
            this._gl.readPixels(0, 0, width, height, this._gl.RGBA, this._gl.UNSIGNED_BYTE, pixels);
            if (invalidate) {
                destination.invalidateGPU();
            }
        }
        // restore back buffer bounding to draw framebufer, but only for sync opps
        if ((rt === null || rt === void 0 ? void 0 : rt.multisampled) && !async) {
            this._gl.bindFramebuffer(this._gl.FRAMEBUFFER, rt.framebuffer);
        }
        // for any case return a promise, nut for sync it will be undef;
        return promise;
    };
    ContextWebGL.prototype.drawIndices = function (mode, indexBuffer, firstIndex, numIndices) {
        if (firstIndex === void 0) { firstIndex = 0; }
        if (numIndices === void 0) { numIndices = -1; }
        if (!this._drawing)
            throw 'Need to clear before drawing if the buffer has not been cleared since the last present() call.';
        // updata blend before draw, because blend state can mutated a more times
        // reduce a state changes
        this.updateBlendStatus();
        // check, that VAO not bounded
        if (!this._hasVao
            || !this._vaoContext._lastBoundedVao) {
            if (this.lastBoundedIndexBuffer !== indexBuffer.glBuffer) {
                this._gl.bindBuffer(this._gl.ELEMENT_ARRAY_BUFFER, indexBuffer.glBuffer);
            }
            this.lastBoundedIndexBuffer = indexBuffer.glBuffer;
        }
        mode = _DEBUG_renderMode === 'line' ? ContextGLDrawMode.LINES : mode;
        this._gl.drawElements(this._drawModeMap[mode], (numIndices == -1) ? indexBuffer.numIndices : numIndices, this._gl.UNSIGNED_SHORT, firstIndex * 2);
    };
    ContextWebGL.prototype.bindIndexBuffer = function (indexBuffer) {
        this._gl.bindBuffer(this._gl.ELEMENT_ARRAY_BUFFER, indexBuffer.glBuffer);
        this.lastBoundedIndexBuffer = indexBuffer.glBuffer;
    };
    ContextWebGL.prototype.drawVertices = function (mode, firstVertex, numVertices) {
        if (firstVertex === void 0) { firstVertex = 0; }
        if (numVertices === void 0) { numVertices = -1; }
        if (!this._drawing)
            throw 'Need to clear before drawing if the buffer has not been cleared since the last present() call.';
        if (numVertices == 0)
            return;
        // updata blend before draw, because blend state can mutated a more times
        // reduce a state changes
        this.updateBlendStatus();
        mode = _DEBUG_renderMode === 'line' ? ContextGLDrawMode.LINES : mode;
        this._gl.drawArrays(this._drawModeMap[mode], firstVertex, numVertices);
        // todo: this check should not be needed.
        // for now it is here to prevent ugly gpu warnings when trying to render numVertices=0
        if (numVertices == 0)
            return;
    };
    ContextWebGL.prototype.present = function () {
        //this._drawing = false;
        this._fenceContext && this._fenceContext.tick();
    };
    ContextWebGL.prototype.setBlendState = function (enable) {
        this._blendState.setAt(0, +enable);
    };
    ContextWebGL.prototype.setBlendFactors = function (sourceFactor, destinationFactor) {
        var src = this._blendFactorMap[sourceFactor];
        var dst = this._blendFactorMap[destinationFactor];
        var gl = this._gl;
        this._blendState.set(+true, gl.FUNC_ADD, src, dst);
    };
    ContextWebGL.prototype.setColorMask = function (red, green, blue, alpha) {
        if (this._colorMapState.set(red, green, blue, alpha)) {
            this._gl.colorMask(red, green, blue, alpha);
        }
    };
    ContextWebGL.prototype.setCulling = function (triangleFaceToCull, coordinateSystem) {
        if (coordinateSystem === void 0) { coordinateSystem = CoordinateSystem.LEFT_HANDED; }
        if (triangleFaceToCull === ContextGLTriangleFace.NONE) {
            this._cullState.setAt(0, +false) && this._gl.disable(this._gl.CULL_FACE);
        }
        else {
            this._cullState.setAt(0, +true) && this._gl.enable(this._gl.CULL_FACE);
            var face = this.translateTriangleFace(triangleFaceToCull, coordinateSystem);
            this._cullState.setAt(1, face) && this._gl.cullFace(face);
        }
    };
    // TODO ContextGLCompareMode
    ContextWebGL.prototype.setDepthTest = function (depthMask, passCompareMode) {
        var mode = this._compareModeMap[passCompareMode];
        this._depthState.setAt(1, +depthMask) && this._gl.depthMask(depthMask);
        this._depthState.setAt(2, mode) && this._gl.depthFunc(mode);
    };
    ContextWebGL.prototype.setViewport = function (x, y, width, height) {
        if (this._viewportState.set(x, y, width, height)) {
            this._gl.viewport(x, y, width, height);
        }
    };
    ContextWebGL.prototype.enableDepth = function () {
        this._depthState.setAt(0, +true) && this._gl.enable(this._gl.DEPTH_TEST);
    };
    ContextWebGL.prototype.disableDepth = function () {
        this._depthState.setAt(0, +false) && this._gl.disable(this._gl.DEPTH_TEST);
    };
    ContextWebGL.prototype.enableStencil = function () {
        this._stencilState.setAt(0, +true) && this._gl.enable(this._gl.STENCIL_TEST);
    };
    ContextWebGL.prototype.disableStencil = function () {
        this._stencilState.setAt(0, +false) && this._gl.disable(this._gl.STENCIL_TEST);
    };
    ContextWebGL.prototype.setStencilActions = function (triangleFace, compareMode, actionOnBothPass, actionOnDepthFail, actionOnDepthPassStencilFail, coordinateSystem) {
        if (triangleFace === void 0) { triangleFace = ContextGLTriangleFace.FRONT_AND_BACK; }
        if (compareMode === void 0) { compareMode = ContextGLCompareMode.ALWAYS; }
        if (actionOnBothPass === void 0) { actionOnBothPass = ContextGLStencilAction.KEEP; }
        if (actionOnDepthFail === void 0) { actionOnDepthFail = ContextGLStencilAction.KEEP; }
        if (actionOnDepthPassStencilFail === void 0) { actionOnDepthPassStencilFail = ContextGLStencilAction.KEEP; }
        if (coordinateSystem === void 0) { coordinateSystem = CoordinateSystem.LEFT_HANDED; }
        this._separateStencil = triangleFace != ContextGLTriangleFace.FRONT_AND_BACK;
        var compareModeGL = this._compareModeMap[compareMode];
        var fail = this._stencilActionMap[actionOnDepthPassStencilFail];
        var zFail = this._stencilActionMap[actionOnDepthFail];
        var pass = this._stencilActionMap[actionOnBothPass];
        if (!this._separateStencil) {
            this._stencilCompareMode = compareModeGL;
            this._gl.stencilFunc(compareModeGL, this._stencilReferenceValue, this._stencilReadMask);
            this._gl.stencilOp(fail, zFail, pass);
        }
        else if (triangleFace == ContextGLTriangleFace.BACK) {
            this._stencilCompareModeBack = compareModeGL;
            this._gl.stencilFuncSeparate(this._gl.BACK, compareModeGL, this._stencilReferenceValue, this._stencilReadMask);
            this._gl.stencilOpSeparate(this._gl.BACK, fail, zFail, pass);
        }
        else if (triangleFace == ContextGLTriangleFace.FRONT) {
            this._stencilCompareModeFront = compareModeGL;
            this._gl.stencilFuncSeparate(this._gl.FRONT, compareModeGL, this._stencilReferenceValue, this._stencilReadMask);
            this._gl.stencilOpSeparate(this._gl.FRONT, fail, zFail, pass);
        }
    };
    ContextWebGL.prototype.setStencilReferenceValue = function (referenceValue, readMask, writeMask) {
        if (readMask === void 0) { readMask = 0xFF; }
        if (writeMask === void 0) { writeMask = 0xFF; }
        this._stencilReferenceValue = referenceValue;
        this._stencilReadMask = readMask;
        if (this._separateStencil) {
            this._gl.stencilFuncSeparate(this._gl.FRONT, this._stencilCompareModeFront, referenceValue, readMask);
            this._gl.stencilFuncSeparate(this._gl.BACK, this._stencilCompareModeBack, referenceValue, readMask);
        }
        else {
            this._gl.stencilFunc(this._stencilCompareMode, referenceValue, readMask);
        }
        this._gl.stencilMask(writeMask);
    };
    ContextWebGL.prototype.setProgram = function (program) {
        // kill focus when program is same
        if (this._currentProgram === program) {
            return;
        }
        //TODO decide on construction/reference resposibilities
        this._currentProgram = program;
        program.focusProgram();
    };
    ContextWebGL.prototype.setProgramConstantsFromArray = function (programType, data) {
        if (data.length) {
            this._currentProgram.uniform4fv(programType, data);
        }
    };
    ContextWebGL.prototype.setScissorRectangle = function (rect) {
        if (!rect) {
            this._gl.disable(this._gl.SCISSOR_TEST);
            return;
        }
        var isRT = !!this._texContext._renderTarget;
        // for framebuffer we use framebuffer size without internal scale
        var pr = isRT ? 1 : this._pixelRatio;
        // not require flip when renderer to RT, because already flipped
        var targetY = isRT
            ? rect.y
            : this._height - (rect.y + rect.height) * pr;
        this._gl.enable(this._gl.SCISSOR_TEST);
        this._gl.scissor(rect.x * pr, targetY, rect.width * pr, rect.height * pr);
    };
    ContextWebGL.prototype.setTextureAt = function (sampler, texture) {
        var id = this._texContext.setTextureAt(sampler, texture);
        // id - is real sampler id, because texture context can change id of sasmpler
        // or return -1 when texture unbounded
        if (id >= 0) {
            this._currentProgram.uniform1i(ContextGLProgramType.SAMPLER, sampler, id);
        }
    };
    ContextWebGL.prototype.setSamplerStateAt = function (sampler, wrap, filter, mipfilter) {
        // proxy
        this._texContext.setSamplerStateAt(sampler, wrap, filter, mipfilter);
    };
    ContextWebGL.prototype.setVertexBufferAt = function (index, buffer, bufferOffset, format) {
        if (bufferOffset === void 0) { bufferOffset = 0; }
        if (format === void 0) { format = 4; }
        var location = this._currentProgram ? this._currentProgram.getAttribLocation(index) : -1;
        // when we try bind any buffers without VAO we should unbound VAO
        // othwerwithe we bind a buffer to vao instead main contex
        if (this.hasVao && this._vaoContext._isRequireUnbound) {
            this._vaoContext.unbindVertexArrays();
        }
        // disable location, OS will fire error when loadings invalid buffer to it
        // location - is index of buffer location inside shader
        // index - attrib location for binding.
        // FOR OSx - IT CAN BE DIFFERENT
        if (!buffer) {
            if (location > -1) {
                this._gl.disableVertexAttribArray(index);
            }
            return;
        }
        //buffer may not have changed if concatenated buffers are being used
        if (this._currentArrayBuffer != buffer || (this.hasVao && this._vaoContext._lastBoundedVao)) {
            this._currentArrayBuffer = buffer;
            this._gl.bindBuffer(this._gl.ARRAY_BUFFER, buffer ? buffer.glBuffer : null);
        }
        var properties = this._vertexBufferMap[format];
        this._gl.enableVertexAttribArray(location);
        this._gl.vertexAttribPointer(location, properties.size, properties.type, properties.normalized, buffer.dataPerVertex, bufferOffset);
    };
    ContextWebGL.prototype.setRenderToTexture = function (target, enableDepthAndStencil, antiAlias, surfaceSelector, mipmapSelector) {
        if (enableDepthAndStencil === void 0) { enableDepthAndStencil = false; }
        if (antiAlias === void 0) { antiAlias = 0; }
        if (surfaceSelector === void 0) { surfaceSelector = 0; }
        if (mipmapSelector === void 0) { mipmapSelector = 0; }
        // proxy
        this._texContext.setRenderToTexture(target, enableDepthAndStencil, antiAlias, surfaceSelector, mipmapSelector);
    };
    ContextWebGL.prototype.setRenderToBackBuffer = function () {
        this._texContext.setRenderToBackBuffer();
    };
    ContextWebGL.prototype.restoreRenderTarget = function () {
        this._texContext.restoreRenderTarget();
    };
    ContextWebGL.prototype.copyToTexture = function (target, rect, destPoint) {
        if (!this._texContext._renderTarget) {
            throw '[ContextWebGL] Try to copy from invalid frambuffer';
        }
        this._texContext.presentFrameBufferTo(this._texContext._renderTarget, target, rect, destPoint);
    };
    ContextWebGL.prototype.updateBlendStatus = function () {
        var bs = this._blendState;
        if (!bs.dirty) {
            return;
        }
        var v = bs.values;
        var delta = bs.deltaDirty();
        // now fixedValues = values;
        bs.lock(true);
        if (v[0]) {
            delta[0] && this._gl.enable(this._gl.BLEND);
            // always ADD
            (delta[1] || delta[0]) && this._gl.blendEquation(v[1]);
            (delta[2] || delta[3] || delta[0]) && this._gl.blendFunc(v[2], v[3]);
        }
        else {
            delta[0] && this._gl.disable(this._gl.BLEND);
        }
    };
    ContextWebGL.prototype.finish = function () {
        this._gl.flush();
        this._gl.finish();
        this._fenceContext && this._fenceContext.tick();
    };
    ContextWebGL.prototype.translateTriangleFace = function (triangleFace, coordinateSystem) {
        switch (triangleFace) {
            case ContextGLTriangleFace.BACK:
                return (coordinateSystem == CoordinateSystem.LEFT_HANDED) ? this._gl.FRONT : this._gl.BACK;
            case ContextGLTriangleFace.FRONT:
                return (coordinateSystem == CoordinateSystem.LEFT_HANDED) ? this._gl.BACK : this._gl.FRONT;
            case ContextGLTriangleFace.FRONT_AND_BACK:
                return this._gl.FRONT_AND_BACK;
            default:
                throw 'Unknown ContextGLTriangleFace type.'; // TODO error
        }
    };
    ContextWebGL.MAX_SAMPLERS = 8;
    ContextWebGL.modulo = 0;
    return ContextWebGL;
}());
export { ContextWebGL };
var VertexBufferProperties = /** @class */ (function () {
    function VertexBufferProperties(size, type, normalized) {
        this.size = size;
        this.type = type;
        this.normalized = normalized;
    }
    return VertexBufferProperties;
}());
export { VertexBufferProperties };
